import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Alert, Button, Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import ProductGallery from './ProductGallery';
import ProductPriceBlock from './ProductPriceBlock';
import SiteHeader from './SiteHeader';
import { getProductGalleryThumb, getProductOgImageUrl } from '../utils/productMedia';
import { setPageOgMeta } from '../utils/ogMeta';
import { ENCAJA_LOGIN_URL } from '../utils/encajaBrand';
import './Landing.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ProductWhatsAppIcon = () => (
  <svg className="product-page-whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const STARTER_SLUG = 'web-comercial-inicial';

const ENCAJA_PLAN_KEYS = {
  'encaja-starter': {
    sections: [
      'productPage.encajaStarterFeature1',
      'productPage.encajaStarterFeature2',
      'productPage.encajaStarterFeature3'
    ],
    conditions: [
      'productPage.encajaCondition1',
      'productPage.encajaCondition2',
      'productPage.encajaCondition3'
    ]
  },
  'encaja-business': {
    sections: [
      'productPage.encajaBusinessFeature1',
      'productPage.encajaBusinessFeature2',
      'productPage.encajaBusinessFeature3'
    ],
    conditions: [
      'productPage.encajaCondition1',
      'productPage.encajaCondition2',
      'productPage.encajaCondition3'
    ]
  },
  'encaja-professional': {
    sections: [
      'productPage.encajaProfessionalFeature1',
      'productPage.encajaProfessionalFeature2',
      'productPage.encajaProfessionalFeature3'
    ],
    conditions: [
      'productPage.encajaCondition1',
      'productPage.encajaCondition2',
      'productPage.encajaCondition3'
    ]
  },
  'encaja-enterprise': {
    sections: [
      'productPage.encajaEnterpriseFeature1',
      'productPage.encajaEnterpriseFeature2',
      'productPage.encajaEnterpriseFeature3'
    ],
    conditions: [
      'productPage.encajaEnterpriseCondition1',
      'productPage.encajaEnterpriseCondition2',
      'productPage.encajaEnterpriseCondition3'
    ]
  }
};

const BUNDLE_KEYS = {
  [STARTER_SLUG]: {
    taglineKey: 'productPage.starterTagline',
    sections: [
      'productPage.starterSection1',
      'productPage.starterSection2',
      'productPage.starterSection3',
      'productPage.starterSection4',
      'productPage.starterSection5'
    ],
    infra: [
      'productPage.starterInfra1',
      'productPage.starterInfra2',
      'productPage.starterInfra3'
    ],
    conditions: [
      'productPage.starterCondition1',
      'productPage.starterCondition2',
      'productPage.starterCondition3',
      'productPage.starterCondition4'
    ]
  }
};

function sanitizePhone(raw) {
  return String(raw || '').replace(/\D/g, '');
}

function ProductShell({ children }) {
  useEffect(() => {
    document.documentElement.classList.add('product-page-active');
    document.body.classList.add('product-page-active');
    return () => {
      document.documentElement.classList.remove('product-page-active');
      document.body.classList.remove('product-page-active');
    };
  }, []);

  return (
    <div className="product-page-shell landing-theme-dark">
      <div className="product-page-backdrop" aria-hidden />
      <SiteHeader variant="marketing" />
      <div className="site-main-with-header">{children}</div>
    </div>
  );
}

function FeatureBulletList({ items, language }) {
  return (
    <ul className="product-feature-list mb-0">
      {items.map((key) => (
        <li key={key}>{t(key, language)}</li>
      ))}
    </ul>
  );
}

function TrustPills({ language }) {
  const keys = ['productPage.trustBadge1', 'productPage.trustBadge2', 'productPage.trustBadge3'];
  return (
    <div className="product-page-trust">
      {keys.map((key) => (
        <span key={key} className="product-page-trust-pill">{t(key, language)}</span>
      ))}
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const location = useLocation();
  const servicesBase = language === 'en' ? '/services' : '/servicios';
  const isPreview = new URLSearchParams(location.search).get('preview') === '1';

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({ paypal: false, binanceDeposit: false });
  const [whatsappBolivia, setWhatsappBolivia] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadProducts = async () => {
      try {
        if (isPreview && slug) {
          const previewRes = await fetch(
            `${API_BASE}/api/catalog/products/slug/${encodeURIComponent(slug)}?preview=1`,
            { credentials: 'include' }
          );
          if (!previewRes.ok) throw new Error('products_fetch_failed');
          const previewData = await previewRes.json();
          const previewProduct = previewData?.product || null;

          const activeRes = await fetch(`${API_BASE}/api/catalog/products?active=true`);
          const activeData = activeRes.ok ? await activeRes.json() : { products: [] };
          const activeList = Array.isArray(activeData?.products) ? activeData.products : [];

          if (cancelled) return;
          if (previewProduct) {
            const merged = [previewProduct, ...activeList.filter((p) => p.id !== previewProduct.id)];
            setProducts(merged);
          } else {
            setProducts(activeList);
          }
          return;
        }

        const res = await fetch(`${API_BASE}/api/catalog/products?active=true`, { credentials: 'include' });
        if (!res.ok) throw new Error('products_fetch_failed');
        const data = await res.json();
        if (cancelled) return;
        setProducts(Array.isArray(data?.products) ? data.products : []);
      } catch {
        if (!cancelled) setError(t('productPage.errorLoad', language));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProducts();
    return () => { cancelled = true; };
  }, [language, slug, isPreview]);

  useEffect(() => {
    fetch(`${API_BASE}/api/checkout/product/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        setPaymentConfig({
          paypal: !!data.paypal,
          binanceDeposit: !!data.binanceDeposit
        });
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cfg?.siteContact?.whatsappBolivia) {
          setWhatsappBolivia(cfg.siteContact.whatsappBolivia);
        }
      })
      .catch(() => {});
  }, []);

  const product = useMemo(
    () => (products || []).find((p) => String(p.slug || '') === String(slug || '')),
    [products, slug]
  );

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return (products || [])
      .filter((p) => p.id !== product.id && p.category_id === product.category_id)
      .slice(0, 3);
  }, [products, product]);

  const bundleKeys = BUNDLE_KEYS[product?.slug] || ENCAJA_PLAN_KEYS[product?.slug] || null;
  const isEncajaPlan = product?.category_slug === 'encaja';
  const isEncajaEnterprise = product?.slug === 'encaja-enterprise';
  const genericIncludes = [
    'productPage.genericInclude1',
    'productPage.genericInclude2',
    'productPage.genericInclude3'
  ];

  const checkoutHref = `/checkout/product?slug=${encodeURIComponent(product?.slug || slug || '')}`;
  const hasPayment = paymentConfig.paypal || paymentConfig.binanceDeposit;
  const canPayNow = hasPayment && !!product?.direct_checkout;
  const whatsappHref = useMemo(() => {
    const phone = sanitizePhone(whatsappBolivia);
    if (!phone) return '';
    const text = encodeURIComponent(
      tf('productPage.whatsappPrefill', { productName: product?.name || '' }, language)
    );
    return `https://wa.me/${phone}?text=${text}`;
  }, [whatsappBolivia, product?.name, language]);

  const tagline = bundleKeys?.taglineKey
    ? t(bundleKeys.taglineKey, language)
    : (product?.description || t('productPage.defaultDescription', language)).slice(0, 180);

  const highlightItems = bundleKeys
    ? bundleKeys.sections.slice(0, 3)
    : genericIncludes;

  useEffect(() => {
    if (!product) return;
    const pageTitle = `${product.name} | ${t('productPage.brandSuffix', language)}`;
    const description = (product.description || t('productPage.defaultDescription', language)).slice(0, 160);
    const pageUrl = `${window.location.origin}${location.pathname}`;
    const ogImage = getProductOgImageUrl(product, product.slug);
    setPageOgMeta({
      title: pageTitle,
      description,
      image: ogImage,
      url: pageUrl,
      type: 'product'
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);
  }, [product, language, location.pathname]);

  if (loading) {
    return (
      <ProductShell>
        <Container className="product-page py-5 text-center">
          <Spinner animation="border" variant="light" />
        </Container>
      </ProductShell>
    );
  }

  if (error) {
    return (
      <ProductShell>
        <Container className="product-page py-4">
          <Alert variant="danger">{error}</Alert>
        </Container>
      </ProductShell>
    );
  }

  if (!product) {
    return (
      <ProductShell>
        <Container className="product-page py-4">
          <Alert variant="warning">{t('productPage.notFound', language)}</Alert>
          <Link to="/" className="landing-news-all-link d-inline-flex">
            {t('productPage.backHome', language)}
          </Link>
        </Container>
      </ProductShell>
    );
  }

  return (
    <ProductShell>
      <Container className="product-page py-4 py-lg-5">
        {isPreview && !product?.active && (
          <Alert variant="info" className="mb-4">
            {t('blogAdmin.productPreviewNote', language)}
          </Alert>
        )}
        <nav className="product-page-breadcrumb small mb-4" aria-label="Breadcrumb">
          <Link to="/">{t('productPage.breadcrumbHome', language)}</Link>
          <span className="product-page-breadcrumb-sep">/</span>
          <Link to="/#servicios">{t('productPage.breadcrumbServices', language)}</Link>
          <span className="product-page-breadcrumb-sep">/</span>
          <span aria-current="page">{product.name}</span>
        </nav>

        <Row className="g-4 g-lg-5 product-page-top align-items-start">
          <Col lg={6}>
            <ProductGallery slug={product.slug} language={language} productName={product.name} product={product} />
          </Col>

          <Col lg={6}>
            <div className="product-page-buybox checkout-shell-card sticky-lg-top">
              <span className="product-page-category-pill">
                {product.category_name || t('productPage.noCategory', language)}
              </span>
              <h1 className="product-page-title">{product.name}</h1>
              <TrustPills language={language} />

              <div className="product-page-price-row">
                <ProductPriceBlock product={product} language={language} variant="page" />
              </div>
              <p className="product-page-price-note">
                {isEncajaPlan
                  ? t('productPage.encajaMonthlyPriceNote', language)
                  : t('productPage.priceNote', language)}
              </p>

              <p className="product-page-tagline">{tagline}</p>

              <ul className="product-page-highlights">
                {highlightItems.map((key) => (
                  <li key={key}>{t(key, language)}</li>
                ))}
              </ul>

              <div className="product-page-cta-row">
                {isEncajaPlan ? (
                  <>
                    {!isEncajaEnterprise ? (
                      <a
                        href={ENCAJA_LOGIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="landing-btn-primary border-0 flex-grow-1 text-center text-decoration-none"
                      >
                        {t('productPage.encajaTrialCta', language)}
                      </a>
                    ) : null}
                    {whatsappHref ? (
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="product-page-whatsapp-btn flex-grow-1"
                      >
                        <ProductWhatsAppIcon />
                        <span>
                          {isEncajaEnterprise
                            ? t('productPage.encajaSalesCta', language)
                            : t('productPage.encajaContractCta', language)}
                        </span>
                      </a>
                    ) : null}
                  </>
                ) : canPayNow ? (
                  <Button as={Link} to={checkoutHref} className="landing-btn-primary border-0 flex-grow-1">
                    {t('productPage.payNowCta', language)}
                  </Button>
                ) : hasPayment ? (
                  <Alert variant="info" className="small border-0 mb-0 flex-grow-1">
                    {t('productPage.checkoutNotEnabled', language)}
                  </Alert>
                ) : (
                  <Alert variant="warning" className="small border-0 mb-0 flex-grow-1">
                    {t('productPage.noPaymentMethods', language)}
                  </Alert>
                )}
                {!isEncajaPlan && whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-page-whatsapp-btn flex-grow-1"
                  >
                    <ProductWhatsAppIcon />
                    <span>{t('productPage.whatsappCta', language)}</span>
                  </a>
                )}
              </div>
              <p className="product-page-login-hint">
                {isEncajaPlan
                  ? t('productPage.encajaLoginHint', language)
                  : t('productPage.loginHint', language)}
              </p>
            </div>
          </Col>
        </Row>

        <section className="product-page-details mt-5 pt-lg-2">
          <h2 className="product-page-section-title">{t('productPage.detailsTitle', language)}</h2>
          <Row className="g-4">
            <Col md={6}>
              <Card className="border-0 checkout-shell-card shadow-none h-100">
                <Card.Body>
                  <h3 className="h6 fw-bold text-white mb-3">
                    {bundleKeys ? t('productPage.includesTitle', language) : t('productPage.summaryTitle', language)}
                  </h3>
                  <FeatureBulletList
                    language={language}
                    items={bundleKeys ? bundleKeys.sections : genericIncludes}
                  />
                </Card.Body>
              </Card>
            </Col>
            {bundleKeys && (
              <Col md={6}>
                <Card className="border-0 checkout-shell-card shadow-none h-100">
                  <Card.Body>
                    <h3 className="h6 fw-bold text-white mb-3">{t('productPage.infrastructureTitle', language)}</h3>
                    <FeatureBulletList language={language} items={bundleKeys.infra} />
                  </Card.Body>
                </Card>
              </Col>
            )}
            <Col md={6}>
              <Card className="border-0 checkout-shell-card shadow-none h-100">
                <Card.Body>
                  <h3 className="h6 fw-bold text-white mb-2">{t('productPage.conditionsTitle', language)}</h3>
                  <FeatureBulletList
                    language={language}
                    items={bundleKeys ? bundleKeys.conditions : genericIncludes}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-0 checkout-shell-card shadow-none h-100">
                <Card.Body>
                  <h3 className="h6 fw-bold text-white">{t('productPage.processTitle', language)}</h3>
                  <p className="product-page-muted small mb-3">{t('productPage.problemBody', language)}</p>
                  <ul className="product-feature-list small product-page-muted mb-4">
                    <li>{t('productPage.processOne', language)}</li>
                    <li>{t('productPage.processTwo', language)}</li>
                    <li>{t('productPage.processThree', language)}</li>
                  </ul>
                  <h3 className="h6 fw-bold text-white">{t('productPage.deliverablesTitle', language)}</h3>
                  <ul className="product-feature-list small product-page-muted mb-3">
                    <li>{t('productPage.deliverableOne', language)}</li>
                    <li>{t('productPage.deliverableTwo', language)}</li>
                    <li>{t('productPage.deliverableThree', language)}</li>
                  </ul>
                  <h3 className="h6 fw-bold text-white">{t('productPage.timelineTitle', language)}</h3>
                  <p className="small product-page-muted mb-0">{t('productPage.timelineBody', language)}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {relatedProducts.length > 0 && (
          <section className="product-page-related mt-5 pt-4">
            <h2 className="product-page-section-title text-center mb-4">{t('productPage.relatedTitle', language)}</h2>
            <Row className="g-4">
              {relatedProducts.map((item) => {
                const thumb = getProductGalleryThumb(item, item.slug, language);
                return (
                  <Col sm={6} lg={4} key={item.id}>
                    <Card className="product-related-card h-100 border-0 checkout-shell-card shadow-none">
                      {thumb && (
                        <div className="product-related-card-media">
                          <img src={thumb.src} alt={thumb.alt || ''} />
                        </div>
                      )}
                      <Card.Body className="d-flex flex-column">
                        <p className="fw-semibold mb-2 text-white">{item.name}</p>
                        <p className="small product-page-muted mb-3 flex-grow-1">
                          {(item.description || t('productPage.defaultDescription', language)).slice(0, 100)}
                          …
                        </p>
                        <p className="small fw-semibold text-white mb-3">
                          {Number(item.price).toFixed(2)} {item.currency || 'USD'}
                        </p>
                        <Link
                          to={`${servicesBase}/${item.slug}`}
                          className="landing-news-all-link text-center small py-2 mt-auto"
                        >
                          {t('productPage.relatedCta', language)}
                        </Link>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </section>
        )}
      </Container>
    </ProductShell>
  );
}
